"use client";

import { isPrivateCapabilityPath } from "@/lib/booking/sharedBooking";
import { usePathname, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useRef } from "react";
import { useGlobalStore } from "@/app/context/globalStoreProvider";
import { isAuthPage } from "@/lib/apiUtils";

const EventTracker = () => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { user } = useGlobalStore();
  const abortControllerRef = useRef<AbortController | null>(null);

  const trackEvent = useCallback(async () => {
    // Skip tracking on auth pages to prevent fetch failures during redirects
    if (isAuthPage(pathname) || isPrivateCapabilityPath(pathname)) {
      return;
    }

    if (process.env.NEXT_PUBLIC_ENVIRONMENT !== "production") {
      return;
    }

    const fbclid = searchParams.get("fbclid");
    if (user?.data.optOut !== true) {
      // Abort any previous pending request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new AbortController for this request
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      try {
        await fetch("/api/public/event", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            eventName: "PageView",
            eventData: { page_path: pathname, fbclid },
          }),
          signal: abortController.signal,
        });
      } catch (error) {
        // Silently handle AbortError (navigation interruptions) - don't log or throw
        // Log other errors in development only to prevent Sentry noise for expected failures
        if (error instanceof Error && error.name !== "AbortError") {
          // Only log non-abort errors in development
          if (process.env.NODE_ENV === "development") {
            // eslint-disable-next-line no-console
            console.debug(
              "[EventTracker] Failed to track event:",
              error.message
            );
          }
        }
      } finally {
        // Clear the abort controller reference if it's still the current one
        if (abortControllerRef.current === abortController) {
          abortControllerRef.current = null;
        }
      }
    }
  }, [pathname, searchParams, user?.data.optOut]);

  useEffect(() => {
    trackEvent();
  }, [pathname, searchParams, trackEvent]);

  // Cleanup: abort any pending request on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, []);

  return null;
};

export default EventTracker;
